import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { TrendingUp, Users, Package, DollarSign, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';

const SalesPerformancePage = () => {
  const [periodType, setPeriodType] = useState('monthly');
  const [reportType, setReportType] = useState('period');
  const [loading, setLoading] = useState(false);
  const [periodData, setPeriodData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;


  const loadReportData = useCallback(async () => {
    setLoading(true);
    try {
      switch(reportType) {
        case 'period':
          await fetchSalesByPeriod();
          break;
        case 'customer':
          await fetchSalesByCustomer();
          break;
        case 'product':
          await fetchSalesByProduct();
          break;
        case 'comparison':
          await fetchOrderVsInvoice();
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line
  }, [reportType, periodType]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const fetchSalesByPeriod = async () => {
    const response = await fetch(`${backendUrl}/api/sales-reports/sales-by-period?period_type=${periodType}`);
    const data = await response.json();
    setPeriodData(data);
  };

  const fetchSalesByCustomer = async () => {
    const response = await fetch(`${backendUrl}/api/sales-reports/sales-by-customer?top_n=20`);
    const data = await response.json();
    setCustomerData(data);
  };

  const fetchSalesByProduct = async () => {
    const response = await fetch(`${backendUrl}/api/sales-reports/sales-by-product?top_n=20`);
    const data = await response.json();
    setProductData(data);
  };

  const fetchOrderVsInvoice = async () => {
    const response = await fetch(`${backendUrl}/api/sales-reports/order-vs-invoice`);
    const data = await response.json();
    setComparisonData(data);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Sales Performance Reports</h1>
            <p className="text-gray-600">Comprehensive sales analytics and insights</p>
          </div>
          <Button onClick={loadReportData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Report Type Selector */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Report Type</label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="period">Sales by Period</SelectItem>
                  <SelectItem value="customer">Top Customers</SelectItem>
                  <SelectItem value="product">Top Products</SelectItem>
                  <SelectItem value="comparison">Order vs Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reportType === 'period' && (
              <div>
                <label className="block text-sm font-medium mb-2">Period Type</label>
                <Select value={periodType} onValueChange={setPeriodType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Period Report */}
        {reportType === 'period' && periodData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center">
                    <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                    Total Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(periodData.total_revenue)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2 text-orange-600" />
                    Total Orders
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{periodData.total_orders}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Package className="w-4 h-4 mr-2 text-orange-600" />
                    Total Quantity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{periodData.total_quantity.toFixed(0)}</div>
                </CardContent>
              </Card>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold">Sales by {periodType.charAt(0).toUpperCase() + periodType.slice(1)} Period</h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {periodData.periods.map((period) => (
                    <TableRow key={period.period}>
                      <TableCell className="font-medium">{period.period}</TableCell>
                      <TableCell className="text-right">{period.orders}</TableCell>
                      <TableCell className="text-right">{period.quantity.toFixed(0)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(period.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Customer Report */}
        {reportType === 'customer' && customerData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Users className="w-4 h-4 mr-2 text-orange-600" />
                    Total Customers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{customerData.total_customers}</div>
                </CardContent>
              </Card>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold">Top 20 Customers by Revenue</h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Customer ID</TableHead>
                    <TableHead>Customer Name</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerData.top_customers.map((customer, index) => (
                    <TableRow key={customer.customer_id}>
                      <TableCell className="font-medium">#{index + 1}</TableCell>
                      <TableCell>{customer.customer_id}</TableCell>
                      <TableCell>{customer.customer_name}</TableCell>
                      <TableCell className="text-right">{customer.orders}</TableCell>
                      <TableCell className="text-right">{formatCurrency(customer.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Product Report */}
        {reportType === 'product' && productData && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Top 20 Products by Revenue</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>Material ID</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productData.top_products.map((product, index) => (
                  <TableRow key={product.material_id}>
                    <TableCell className="font-medium">#{index + 1}</TableCell>
                    <TableCell>{product.material_id}</TableCell>
                    <TableCell>{product.description}</TableCell>
                    <TableCell className="text-right">{product.quantity.toFixed(0)}</TableCell>
                    <TableCell className="text-right">{product.orders}</TableCell>
                    <TableCell className="text-right">{formatCurrency(product.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Comparison Report */}
        {reportType === 'comparison' && comparisonData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{comparisonData.total_orders}</div>
                  <div className="text-sm text-gray-500">{formatCurrency(comparisonData.order_value)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{comparisonData.total_invoices}</div>
                  <div className="text-sm text-gray-500">{formatCurrency(comparisonData.invoice_value)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Fulfillment Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{comparisonData.fulfillment_rate.toFixed(1)}%</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Pending Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(comparisonData.pending_value)}</div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SalesPerformancePage;